// Screen rights — the ONE list of gated cards/sections in the app.
//
// Every entry here automatically shows up in Admin → User Rights (grouped by
// `group`), and `canSee(key)` hides that card for users who aren't granted it.
// To add a new screen: add one line here and wrap its card with canSee('<key>').
// No backend change is needed — the server accepts any `screen.*` key.
//
// Management users see everything. Rights are read at login, so a user must
// log out and in again after their rights change.
export const SCREENS = [
  // Main page
  { key: 'screen.home.rw',          label: 'RW card',                   group: 'Main Page' },
  { key: 'screen.home.sbd',         label: 'SBD card',                  group: 'Main Page' },
  { key: 'screen.home.optus',       label: 'OPTUS card',                group: 'Main Page' },
  { key: 'screen.home.gmt_factory', label: 'GMT FACTORY card',          group: 'Main Page' },

  // RW screen
  { key: 'screen.rw.tas',             label: 'TAS',                     group: 'RW' },
  { key: 'screen.rw.gsp',             label: 'GSP',                     group: 'RW' },
  { key: 'screen.rw.development',     label: 'Development',             group: 'RW' },
  { key: 'screen.rw.tas_status',      label: 'TAS Status',              group: 'RW' },
  { key: 'screen.rw.shipment_target', label: 'Monthly Shipment Target', group: 'RW' },

  // RW → TAS
  { key: 'screen.tas.order_in_hand',   label: 'Order in Hand',          group: 'RW · TAS' },
  { key: 'screen.tas.sales',           label: 'Sales',                  group: 'RW · TAS' },
  { key: 'screen.tas.cost_comparison', label: 'Cost Comparison',        group: 'RW · TAS' },
  { key: 'screen.tas.production',      label: 'TAS Production',         group: 'RW · TAS' },
  { key: 'screen.tas.daily_activity',  label: 'Daily Activity',         group: 'RW · TAS' },

  // RW → TAS → Daily Activity
  { key: 'screen.daily.critical',   label: 'Critical',                  group: 'RW · TAS · Daily Activity' },
  { key: 'screen.daily.delayed',    label: 'Delayed',                   group: 'RW · TAS · Daily Activity' },
  { key: 'screen.daily.size_ratio', label: 'Size Ratio',                group: 'RW · TAS · Daily Activity' },
  { key: 'screen.daily.bom',        label: 'BOM',                       group: 'RW · TAS · Daily Activity' },

  // RW → GSP
  { key: 'screen.gsp.order_in_hand',   label: 'Order in Hand',          group: 'RW · GSP' },
  { key: 'screen.gsp.sales',           label: 'Sales',                  group: 'RW · GSP' },
  { key: 'screen.gsp.cost_comparison', label: 'Cost Comparison',        group: 'RW · GSP' },
  { key: 'screen.gsp.fabric_routing',  label: 'Fabric Routing',         group: 'RW · GSP' },

  // SBD (Bangladesh team)
  { key: 'screen.sbd.order_in_hand',   label: 'Order in Hand',          group: 'SBD' },
  { key: 'screen.sbd.upcoming',        label: 'Upcoming Order',         group: 'SBD' },
  { key: 'screen.sbd.factory_assign',  label: 'Factory Assignment',     group: 'SBD' },
  { key: 'screen.sbd.sales_approval',  label: 'Cost Sheet Approval',    group: 'SBD' },
  { key: 'screen.sbd.fi_inspection',   label: 'FI Inspection Approval', group: 'SBD' },
  { key: 'screen.sbd.final_approval',  label: 'Final Costing Approval', group: 'SBD' },
  { key: 'screen.sbd.shipment_update', label: 'Shipment Update',        group: 'SBD' },

  // GMT Factory → one factory
  { key: 'screen.factory.daily_production', label: 'Daily Production',  group: 'GMT Factory' },
  { key: 'screen.factory.fab_inhouse',      label: 'Fab Inhouse',       group: 'GMT Factory' },
  { key: 'screen.factory.cost_sheet',       label: 'Cost Sheet',        group: 'GMT Factory' },
  { key: 'screen.factory.final_costing',    label: 'Final Costing',     group: 'GMT Factory' },
  { key: 'screen.factory.shipment',         label: 'Shipment',          group: 'GMT Factory' },
];

// One right per factory (GMT Factory → that factory's tile). The list of factories
// is live (Admin → User Rights loads it from /admin/factories), so these keys are
// built from the factory's mst_party id rather than listed above.
export const factoryRightKey = id => `screen.factory.unit.${id}`;
export const FACTORY_RIGHTS_GROUP = 'GMT Factory · Factories';

function currentUser() {
  try { return JSON.parse(localStorage.getItem('dj_user')) || {}; } catch { return {}; }
}

// A parent card shows by itself whenever any right under it is ticked, so ticking
// e.g. only Size Ratio opens the way RW → TAS → Daily Activity → Size Ratio.
// parent key → key prefixes of everything beneath it.
const CHILDREN = {
  'screen.home.rw':            ['screen.rw.', 'screen.tas.', 'screen.daily.', 'screen.gsp.'],
  'screen.rw.tas':             ['screen.tas.', 'screen.daily.'],
  'screen.tas.daily_activity': ['screen.daily.'],
  'screen.rw.gsp':             ['screen.gsp.'],
  'screen.home.sbd':           ['screen.sbd.'],
  'screen.home.gmt_factory':   ['screen.factory.'],
};

export function canSee(key) {
  const u = currentUser();
  if (u.isManagement || u.isDemo || u.isAdmin || u.isDev) return true;
  const r = Array.isArray(u.rights) ? u.rights : [];
  return r.includes(key) || (CHILDREN[key] || []).some(p => r.some(k => k.startsWith(p)));
}
